import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { expectedError } from '@nsc/smartteams-shared/api/expected-error.js';
import { toRecipients } from '@nsc/smartteams-shared/api/service-desk-email.js';
import { ServiceDeskRecipients } from '../db/ServiceDeskRecipients.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { SmartTeamsAppPermission, SmartTeamsAppUserGroup } from '../features/permissions/roles-and-permissions.js';
import { getCurrentAppUser } from './_assignee-guard.js';

/** The back-office stores these on the partner company; see SmartTeamsPartnerCompany in @nsc/smartteams. */
interface CompanyServiceDesk {
  serviceDeskTo?: string | null;
  serviceDeskCc?: string[] | null;
}

/**
 * Who the "Email service desk" button addresses for the current user.
 *
 * The recipients are the ones an NSC Admin set on the caller's partner company. If none are set (or the
 * user has no company) they come back empty — the button is still shown and opens a draft with the
 * recipients blank. Only field engineers and admins get an answer: for anyone else this is null and the
 * button is not shown, since customers have no use for it.
 */
export const getServiceDeskRecipients = registerQuery({
  operationName: 'getSmartTeamsAppServiceDeskRecipients',
  type: () => ServiceDeskRecipients,
  params: [],
  resolve: () => async (_gql: BaseQLSelectionSet<ServiceDeskRecipients>) => {
    const allowed = await accessController.can(createPermissionPayload(SmartTeamsAppPermission.TicketRead));
    if (!allowed) throw expectedError('Insufficient permissions');

    const appUser = await getCurrentAppUser();
    if (appUser.userGroup !== SmartTeamsAppUserGroup.FieldEngineer && appUser.userGroup !== SmartTeamsAppUserGroup.Admin) {
      return null;
    }

    let company: CompanyServiceDesk | null = null;
    if (appUser.companyId) {
      const col = await getRawCollection<CompanyServiceDesk & { _id: string; deletedAt?: Date | null }>(
        'smartteams_partnercompanies',
      );
      company = await col.findOne(
        { _id: appUser.companyId, deletedAt: null },
        { projection: { serviceDeskTo: 1, serviceDeskCc: 1 } },
      );
    }

    const resolved = toRecipients(company && { to: company.serviceDeskTo, cc: company.serviceDeskCc });
    // Always an entity for the roles that see the button: an unset company yields empty recipients, so the
    // button still opens the mail program with the subject and body filled and the address left to type.
    return new ServiceDeskRecipients(resolved ?? { to: '', cc: [] });
  },
});
